import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  Video,
  CheckCircle2,
  LockKeyhole,
  Calendar,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui/panel";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status/status-badge";
import { ProviderLogo } from "@/components/providers/destination-provider-logo";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useOrg } from "@/lib/org-context";
import { useExitWizard, useWizardNavigate } from "@/lib/wizard-navigation";
import { useNavigate, useSearchParams } from "react-router";

import {
  loadWizard,
  saveWizard,
  clearWizard,
  type WizardSourceId,
  type WizardState,
} from "./wizard-state";
import {
  scheduleToIntervalSeconds,
  summarizeConfigureSync,
} from "@attendee-sync/connectors";

type SourceEventItem = {
  id: string;
  resourceType: string;
  name: string;
  startTime?: string | null;
  hostEmail?: string | null;
  registrantCount?: number | null;
  alreadyConfigured?: boolean;
};

type DestinationField = {
  key: string;
  label: string;
  secret: boolean;
  help?: string | null;
  required: boolean;
};

type DestinationProviderSetup = {
  provider: string;
  label: string;
  kind: "destination";
  description: string;
  recommendation?: string | null;
  docsUrl?: string | null;
  docsLabel?: string | null;
  fields: DestinationField[];
  configured: boolean;
  credentialId: string | null;
  hintMask: string | null;
  status?: "ga" | "beta" | "disabled";
};

type DestinationCredentialUpsertResult = {
  id: string;
  provider: string;
  label: string;
  hintMask: string | null;
  configured: true;
};

function eventKey(ev: Pick<SourceEventItem, "resourceType" | "id">) {
  return `${ev.resourceType}:${ev.id}`;
}

function formatEventMeta(ev: SourceEventItem): string {
  const kind =
    ev.resourceType === "webinar"
      ? "Webinar"
      : ev.resourceType === "teams_webinar"
        ? "Teams webinar"
        : ev.resourceType === "meet_event"
          ? "Google Meet"
          : ev.resourceType === "calendar_event"
            ? "Calendar event"
            : "Meeting";
  const parts: string[] = [kind];
  if (ev.startTime) {
    try {
      parts.push(new Date(ev.startTime).toLocaleString());
    } catch {
      /* ignore */
    }
  }
  if (ev.registrantCount != null) {
    parts.push(
      `${ev.registrantCount} registrant${ev.registrantCount === 1 ? "" : "s"}`,
    );
  }
  return parts.join(" · ");
}

/** Vite dev only — Zoom Marketplace rejects localhost OAuth redirects. */
function isLocalDevWizard(): boolean {
  return import.meta.env.DEV;
}

function WizardStepFooter({
  onBack,
  primary,
}: {
  onBack?: () => void;
  primary?: ReactNode;
}) {
  const exitWizard = useExitWizard();
  const { orgId } = useOrg();

  return (
    <div className="rs-wizard-step-footer">
      <Button
        variant="link"
        className="rs-wizard-later"
        onClick={() => {
          clearWizard(orgId);
          exitWizard();
        }}
      >
        I&apos;ll Do This Later
      </Button>
      <div className="rs-wizard-step-footer-actions">
        {onBack ? (
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
        ) : null}
        {primary}
      </div>
    </div>
  );
}

export function WizardIntroductionStep() {
  const { orgId, canManage } = useOrg();
  const navigate = useWizardNavigate();
  const nameId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [streamName, setStreamName] = useState(
    () => loadWizard(orgId).streamName,
  );
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!canManage) return;
    const frame = window.requestAnimationFrame(() => {
      nameRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [canManage]);

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  function continueWithName() {
    const trimmed = streamName.trim();
    if (!trimmed) {
      setNameError("Enter a stream name to continue.");
      return;
    }
    const next: WizardState = { ...loadWizard(orgId), streamName: trimmed };
    saveWizard(orgId, next);
    setNameError(null);
    navigate(`/conferences/new/select`);
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-wizard-intro-main">
        <div className="rs-wizard-intro-header">
          <h2 className="text-rs-ink">Create a New Sync Stream</h2>
          <p className="text-sm text-rs-ink-muted">
            A sync stream connects your event registration source to one or more
            destinations, so attendee information can move automatically between
            your systems.
          </p>
        </div>

        <section
          className="rs-wizard-intro-section"
          aria-labelledby={`${nameId}-heading`}
        >
          <p
            id={`${nameId}-heading`}
            className="text-sm font-medium text-rs-ink"
          >
            Give this stream a clear name so you can easily identify it later.
          </p>
          <div className="mt-4 space-y-2">
            <Input
              ref={nameRef}
              id={nameId}
              value={streamName}
              placeholder="Stream Name"
              aria-label="Stream Name"
              onChange={(e) => {
                setStreamName(e.target.value);
                if (nameError) setNameError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  continueWithName();
                }
              }}
              aria-invalid={Boolean(nameError)}
              aria-describedby={
                nameError ? `${nameId}-error ${nameId}-hint` : `${nameId}-hint`
              }
              autoComplete="off"
            />
            {nameError ? (
              <p
                id={`${nameId}-error`}
                role="alert"
                className="text-sm text-rs-danger"
              >
                {nameError}
              </p>
            ) : null}
            <p id={`${nameId}-hint`} className="text-xs text-rs-ink-subtle">
              <em>
                * This name is only used inside AttendeeSync and can be changed
                later.
              </em>
            </p>
          </div>
        </section>

        <div className="flex justify-center items-start w-full mt-8">
          <figure className="rs-wizard-intro-diagram m-0">
            <img
              src={`${import.meta.env.BASE_URL}brand/sync-stream-flow.png`}
              alt="Source such as a meeting or webinar connects through AttendeeSync to a destination such as a CRM or database"
              width={1200}
              height={400}
              style={{
                display: "block",
                margin: "0 auto",
                maxWidth: "100%",
                height: "auto",
              }}
            />
          </figure>
        </div>
      </div>

      <WizardStepFooter
        primary={
          <Button
            disabled={!streamName.trim()}
            onClick={() => continueWithName()}
          >
            Continue
          </Button>
        }
      />
    </div>
  );
}

function ConnectZoomPanel({
  onBack,
  onSkipSource,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [message, setMessage] = useState<string | null>(null);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectZoom() {
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/zoom/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Zoom",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <Video aria-hidden="true" />
          </span>
          <h2 className="text-rs-ink">Connect Your Zoom Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize your Zoom account to see your meetings and webinars.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Secure OAuth connection
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Read meetings and webinars
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Access event and registrant details
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              No password required
            </li>
          </ul>
          <p className="rs-connect-redirect text-rs-ink-muted">
            <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
            redirected to Zoom to authorize access.
          </p>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: Zoom does not allow localhost OAuth redirects.
              You can skip source setup and continue to destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button onClick={() => void connectZoom()}>
              Connect with Zoom
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectGoogleMeetPanel({
  onBack,
  onSkipSource,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [message, setMessage] = useState<string | null>(null);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectGoogle() {
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/google_meet/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Google Meet",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="google_meet"
              label="Google Meet"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Google Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize Google Calendar to list Meet-linked events and sync
            invitees.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Secure OAuth connection
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Read Meet calendar events
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync invitee email and RSVP status
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled sync (no live webhooks in v1)
            </li>
          </ul>
          <p className="rs-connect-redirect text-rs-ink-muted">
            <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
            redirected to Google to authorize access.
          </p>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: Google may reject localhost OAuth redirects.
              You can skip source setup and continue to destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button onClick={() => void connectGoogle()}>
              Connect with Google
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectGoogleFormsPanel({
  onBack,
  onSkipSource,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [message, setMessage] = useState<string | null>(null);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectGoogleForms() {
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/google_forms/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Google Forms",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="google_forms"
              label="Google Forms"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Google Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize Google Forms to sync responses from forms you paste (edit
            URL or file ID). Forms must collect email addresses.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Secure OAuth connection
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Paste a form edit URL or file ID to sync
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync respondent email and answers
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled sync (forms must collect email addresses)
            </li>
          </ul>
          <p className="rs-connect-redirect text-rs-ink-muted">
            <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
            redirected to Google to authorize access.
          </p>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: Google may reject localhost OAuth redirects.
              You can skip source setup and continue to destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button onClick={() => void connectGoogleForms()}>
              Connect with Google
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectLinkedInEventsPanel({
  onBack,
  onSkipSource,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [message, setMessage] = useState<string | null>(null);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectLinkedIn() {
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/linkedin_events/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "LinkedIn Events",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="linkedin_events"
              label="LinkedIn Events"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your LinkedIn Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize LinkedIn to list Company Page events and sync Lead Gen
            form registrants. Live stream viewer lists are not available via
            LinkedIn APIs.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Secure OAuth connection
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Read Company Page events
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync Lead Gen registrant fields
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled sync plus Lead Sync webhooks when configured
            </li>
          </ul>
          <p className="rs-connect-redirect text-rs-ink-muted">
            <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
            redirected to LinkedIn to authorize access. You need ADMINISTRATOR
            or CONTENT_ADMINISTRATOR on the Company Page.
          </p>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: LinkedIn may reject localhost OAuth redirects.
              You can skip source setup and continue to destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button onClick={() => void connectLinkedIn()}>
              Connect with LinkedIn
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectConstantContactPanel({
  onBack,
  onSkipSource,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [message, setMessage] = useState<string | null>(null);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectConstantContact() {
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/constant_contact/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Constant Contact",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="constant_contact"
              label="Constant Contact"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Constant Contact Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize Constant Contact to list Events and sync registrants on a
            schedule. Contact lists and email campaigns are not used as sources.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Secure OAuth connection
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Read Constant Contact Events
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync event registrant fields
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled and manual sync
            </li>
          </ul>
          <p className="rs-connect-redirect text-rs-ink-muted">
            <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
            redirected to Constant Contact to authorize access.
          </p>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: Constant Contact may reject localhost OAuth
              redirects. You can skip source setup and continue to destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button onClick={() => void connectConstantContact()}>
              Connect with Constant Contact
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectEventbritePanel({
  onBack,
  onSkipSource,
  onConnected,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  onConnected: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [mode, setMode] = useState<"choose" | "private_token">("choose");
  const [privateToken, setPrivateToken] = useState("");
  const [reveal, setReveal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [oauthAvailable, setOauthAvailable] = useState(true);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  useEffect(() => {
    void api<{ oauthEnabled?: boolean }>(
      `/api/accounts/${orgId}/sources/eventbrite`,
    )
      .then((res) => setOauthAvailable(res.oauthEnabled !== false))
      .catch(() => setOauthAvailable(false));
  }, [orgId]);

  async function connectOAuth() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/eventbrite/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Eventbrite",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
      setBusy(false);
    }
  }

  async function connectPrivateToken() {
    const token = privateToken.trim();
    if (!token) {
      setMessage("Private token is required.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/accounts/${orgId}/sources/eventbrite/connect`, {
        method: "POST",
        body: JSON.stringify({ privateToken: token }),
      });
      onConnected();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="eventbrite"
              label="Eventbrite"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Eventbrite Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize Eventbrite with OAuth, or paste a private token, to sync
            event attendees into your CRM on a schedule.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              OAuth or private token
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              List organization events
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync attendee profile fields
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled and manual sync
            </li>
          </ul>
          {mode === "choose" ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {oauthAvailable ? (
                <Button disabled={busy} onClick={() => void connectOAuth()}>
                  {busy ? "Connecting…" : "Connect with Eventbrite"}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setMode("private_token");
                  setMessage(null);
                }}
              >
                Use Private Token
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <label className="sr-only" htmlFor="wizard-eventbrite-token">
                Eventbrite Private Token
              </label>
              <div className="flex gap-2">
                <Input
                  id="wizard-eventbrite-token"
                  type={reveal ? "text" : "password"}
                  placeholder="Private Token"
                  aria-label="Eventbrite Private Token"
                  value={privateToken}
                  onChange={(e) => setPrivateToken(e.target.value)}
                  disabled={busy}
                />
                <Button
                  type="button"
                  variant="secondary"
                  aria-label={reveal ? "Hide Token" : "Show Token"}
                  onClick={() => setReveal((v) => !v)}
                >
                  {reveal ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    setMode("choose");
                    setMessage(null);
                  }}
                >
                  Back
                </Button>
                <Button
                  disabled={busy || !privateToken.trim()}
                  onClick={() => void connectPrivateToken()}
                >
                  {busy ? "Connecting…" : "Connect Eventbrite"}
                </Button>
              </div>
            </div>
          )}
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: You can skip source setup and continue to
              destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          allowSkipSource && onSkipSource ? (
            <Button variant="secondary" onClick={onSkipSource}>
              Skip Source
            </Button>
          ) : null
        }
      />
    </div>
  );
}

function ConnectMicrosoftTeamsPanel({
  onBack,
  onSkipSource,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [message, setMessage] = useState<string | null>(null);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectTeams() {
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/microsoft_teams/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Microsoft Teams",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="microsoft_teams"
              label="Microsoft Teams"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Microsoft Teams Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize Microsoft to list Teams webinars you organize (or
            co-organize) and sync registrants on a schedule. Requires
            VirtualEvent.Read.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Secure Microsoft OAuth connection
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              List organizer and co-organizer webinars
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync webinar registrant fields
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled and manual sync
            </li>
          </ul>
          <p className="rs-connect-redirect text-rs-ink-muted">
            <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
            redirected to Microsoft to authorize access.
          </p>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: Microsoft may reject localhost OAuth redirects.
              You can skip source setup and continue to destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button onClick={() => void connectTeams()}>
              Connect with Microsoft
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectMailchimpPanel({
  onBack,
  onSkipSource,
  onConnected,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  onConnected: () => void;
  error?: string | null;
}) {
  const { orgId, organization, canManage } = useOrg();
  const [mode, setMode] = useState<"choose" | "api_key">("choose");
  const [apiKey, setApiKey] = useState("");
  const [reveal, setReveal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [oauthAvailable, setOauthAvailable] = useState(true);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  useEffect(() => {
    void api<{ oauthEnabled?: boolean }>(
      `/api/accounts/${orgId}/sources/mailchimp`,
    )
      .then((res) => setOauthAvailable(res.oauthEnabled !== false))
      .catch(() => setOauthAvailable(false));
  }, [orgId]);

  async function connectOAuth() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/mailchimp/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Mailchimp",
            returnPath: "/conferences/new/select",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
      setBusy(false);
    }
  }

  async function connectApiKey() {
    const key = apiKey.trim();
    if (!key) {
      setMessage("API key is required.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/accounts/${orgId}/sources/mailchimp/connect`, {
        method: "POST",
        body: JSON.stringify({ apiKey: key }),
      });
      onConnected();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="mailchimp"
              label="Mailchimp"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Mailchimp Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Authorize Mailchimp with OAuth, or paste an API key, to sync
            subscribed audience members into your CRM on a schedule.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              OAuth or API key
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync subscribed audience members
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Map merge fields to CRM contacts
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Scheduled and manual sync
            </li>
          </ul>
          {mode === "api_key" ? (
            <div className="mt-4 max-w-xl space-y-2">
              <div className="relative">
                <Input
                  type={reveal ? "text" : "password"}
                  autoComplete="off"
                  placeholder="API Key"
                  aria-label="Mailchimp API Key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                  onClick={() => setReveal((v) => !v)}
                  aria-label={reveal ? "Hide Key" : "Show Key"}
                >
                  {reveal ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <p className="rs-connect-redirect text-rs-ink-muted">
              <LockKeyhole aria-hidden="true" /> You&apos;ll be securely
              redirected to Mailchimp to authorize access, or you can paste an
              API key instead.
            </p>
          )}
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: Mailchimp may reject localhost OAuth redirects.
              You can skip source setup and continue to destinations, or use an
              API key.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={
          mode === "api_key"
            ? () => {
                setMode("choose");
                setApiKey("");
                setMessage(null);
              }
            : onBack
        }
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            {mode === "choose" ? (
              <>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    setMode("api_key");
                    setMessage(null);
                  }}
                >
                  Use API Key
                </Button>
                {oauthAvailable ? (
                  <Button disabled={busy} onClick={() => void connectOAuth()}>
                    {busy ? "Connecting…" : "Connect with Mailchimp"}
                  </Button>
                ) : null}
              </>
            ) : (
              <Button
                disabled={busy || !apiKey.trim()}
                onClick={() => void connectApiKey()}
              >
                {busy ? "Connecting…" : "Connect Mailchimp"}
              </Button>
            )}
          </>
        }
      />
    </div>
  );
}

function ConnectHeySummitPanel({
  onBack,
  onSkipSource,
  onConnected,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  onConnected: () => void;
  error?: string | null;
}) {
  const { orgId, canManage } = useOrg();
  const [token, setToken] = useState("");
  const [reveal, setReveal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectHeySummit() {
    const apiToken = token.trim();
    if (!apiToken) {
      setMessage("API token is required.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/accounts/${orgId}/sources/heysummit/connect`, {
        method: "POST",
        body: JSON.stringify({ apiToken }),
      });
      onConnected();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="heysummit"
              label="HeySummit"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your HeySummit Account</h2>
          <p className="text-sm text-rs-ink-muted">
            Paste the API token from HeySummit Setup → API, MCP &amp; Webhooks.
            AttendeeSync encrypts the token and registers webhooks for
            near-real-time attendee sync.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              Encrypted API token storage
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              List summit events and attendees
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Sync registration and custom questions
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Automatic webhook subscriptions
            </li>
          </ul>
          <div className="mt-4 max-w-md space-y-2 text-left">
            <label className="sr-only" htmlFor="wizard-heysummit-token">
              HeySummit API Token
            </label>
            <div className="relative">
              <Input
                id="wizard-heysummit-token"
                type={reveal ? "text" : "password"}
                autoComplete="off"
                placeholder="API Token"
                aria-label="HeySummit API Token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                disabled={busy}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? "Hide Token" : "Show Token"}
              >
                {reveal ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
          </div>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: you can skip source setup and continue to
              destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button
              disabled={busy || !token.trim()}
              onClick={() => void connectHeySummit()}
            >
              {busy ? "Connecting…" : "Connect HeySummit"}
            </Button>
          </>
        }
      />
    </div>
  );
}

function ConnectFluentFormsPanel({
  onBack,
  onSkipSource,
  onConnected,
  error,
}: {
  onBack: () => void;
  onSkipSource?: () => void;
  onConnected: () => void;
  error?: string | null;
}) {
  const { orgId, canManage } = useOrg();
  const [siteUrl, setSiteUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();
  const alertMessage = message ?? error ?? null;

  async function connectFluentForms() {
    const site = siteUrl.trim();
    const user = username.trim();
    const applicationPassword = password.trim();
    if (!site || !user || !applicationPassword) {
      setMessage("Site URL, username, and Application Password are required.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/accounts/${orgId}/sources/fluent_forms/connect`, {
        method: "POST",
        body: JSON.stringify({
          siteUrl: site,
          username: user,
          applicationPassword,
        }),
      });
      onConnected();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-connect-main">
        <Panel className="rs-connect-copy">
          <span className="rs-zoom-icon">
            <ProviderLogo
              provider="fluent_forms"
              label="Fluent Forms"
              className="size-10"
            />
          </span>
          <h2 className="text-rs-ink">Connect Your Fluent Forms Site</h2>
          <p className="text-sm text-rs-ink-muted">
            Enter your WordPress site URL and an Application Password. After
            connecting, configure a Fluent Forms Pro webhook feed with the URL
            and secret shown on Integrations for live submissions.
          </p>
          <ul className="rs-connect-benefits space-y-2 text-rs-ink-muted">
            <li>
              <CheckCircle2 aria-hidden="true" />
              WordPress Application Password auth
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              List forms and pull submissions
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Map form fields to CRM contacts
            </li>
            <li>
              <CheckCircle2 aria-hidden="true" />
              Optional Pro webhooks for live sync
            </li>
          </ul>
          <div className="mt-4 max-w-md space-y-3 text-left">
            <Input
              type="url"
              autoComplete="url"
              placeholder="https://example.com"
              aria-label="WordPress Site URL"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              disabled={busy}
            />
            <Input
              type="text"
              autoComplete="username"
              placeholder="Username"
              aria-label="WordPress Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={busy}
            />
            <div className="relative">
              <Input
                type={reveal ? "text" : "password"}
                autoComplete="off"
                placeholder="Application Password"
                aria-label="Application Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? "Hide Password" : "Show Password"}
              >
                {reveal ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
          </div>
          {allowSkipSource ? (
            <p className="mt-3 text-sm text-rs-ink-muted">
              Local development: you can skip source setup and continue to
              destinations.
            </p>
          ) : null}
          {alertMessage ? (
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {alertMessage}
            </p>
          ) : null}
        </Panel>
      </div>
      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button
              disabled={
                busy || !siteUrl.trim() || !username.trim() || !password.trim()
              }
              onClick={() => void connectFluentForms()}
            >
              {busy ? "Connecting…" : "Connect Fluent Forms"}
            </Button>
          </>
        }
      />
    </div>
  );
}

function ChooseSourcePanel({
  selectedSource,
  onSelectSource,
  onBack,
  onContinue,
  onSkipSource,
}: {
  selectedSource: WizardSourceId | null;
  onSelectSource: (source: WizardSourceId) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkipSource?: () => void;
}) {
  const sourceListId = useId();
  const allowSkipSource = Boolean(onSkipSource) && isLocalDevWizard();

  return (
    <div className="rs-connect-step">
      <div className="rs-wizard-intro-main">
        <div className="rs-wizard-intro-header">
          <h2 className="text-rs-ink">Choose Your Source</h2>
          <p className="text-sm text-rs-ink-muted">
            Select where AttendeeSync should retrieve attendee and registration
            information for this sync stream.
          </p>
          <p className="mt-2 text-sm text-rs-ink-muted">
            Your source is the system that owns the registration data you want
            to sync.
          </p>
          {allowSkipSource ? (
            <p className="mt-2 text-sm text-rs-ink-muted">
              Local development: OAuth providers may reject localhost redirects,
              so you may skip this step.
            </p>
          ) : null}
        </div>

        <section
          className="rs-wizard-intro-section"
          aria-labelledby={`${sourceListId}-heading`}
        >
          <p
            id={`${sourceListId}-heading`}
            className="text-sm font-medium text-rs-ink"
          >
            Choose the platform you want to connect:
          </p>
          <div
            className="rs-wizard-source-list"
            role="radiogroup"
            aria-labelledby={`${sourceListId}-heading`}
          >
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "zoom"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "zoom" && "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("zoom")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="zoom"
                  label="Zoom"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">Zoom</span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync registrants and attendees from Zoom meetings or webinars.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "google_meet"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "google_meet" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("google_meet")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="google_meet"
                  label="Google Meet"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  Google Meet
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync Calendar invitees from Meet-linked events on a schedule.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "google_forms"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "google_forms" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("google_forms")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="google_forms"
                  label="Google Forms"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  Google Forms
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync form responses that collect email addresses on a
                  schedule.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "linkedin_events"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "linkedin_events" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("linkedin_events")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="linkedin_events"
                  label="LinkedIn Events"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  LinkedIn Events
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync Lead Gen form registrants from Company Page events (not
                  Live viewers).
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "heysummit"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "heysummit" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("heysummit")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="heysummit"
                  label="HeySummit"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">HeySummit</span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync summit attendees via API token with scheduled and webhook
                  sync.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "fluent_forms"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "fluent_forms" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("fluent_forms")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="fluent_forms"
                  label="Fluent Forms"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  Fluent Forms
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync WordPress form submissions via Application Password and
                  optional Pro webhooks.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "constant_contact"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "constant_contact" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("constant_contact")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="constant_contact"
                  label="Constant Contact"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  Constant Contact
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync registrants from Constant Contact Events on a schedule.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "mailchimp"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "mailchimp" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("mailchimp")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="mailchimp"
                  label="Mailchimp"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">Mailchimp</span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync subscribed audience members via OAuth or API key.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "eventbrite"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "eventbrite" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("eventbrite")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="eventbrite"
                  label="Eventbrite"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  Eventbrite
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync event attendees via OAuth or private token on a schedule.
                </span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={selectedSource === "microsoft_teams"}
              className={cn(
                "rs-wizard-source-option",
                selectedSource === "microsoft_teams" &&
                  "rs-wizard-source-option-selected",
              )}
              onClick={() => onSelectSource("microsoft_teams")}
            >
              <span className="rs-wizard-event-radio" aria-hidden="true" />
              <span className="rs-wizard-source-icon" aria-hidden="true">
                <ProviderLogo
                  provider="microsoft_teams"
                  label="Microsoft Teams"
                  className="size-8 border-0 p-0"
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block font-medium text-rs-ink">
                  Microsoft Teams
                </span>
                <span className="mt-0.5 block text-sm text-rs-ink-muted">
                  Sync Teams webinar registrants you organize or co-organize.
                </span>
              </span>
            </button>
          </div>
        </section>
      </div>

      <WizardStepFooter
        onBack={onBack}
        primary={
          <>
            {allowSkipSource && onSkipSource ? (
              <Button variant="secondary" onClick={onSkipSource}>
                Skip Source
              </Button>
            ) : null}
            <Button disabled={!selectedSource} onClick={onContinue}>
              Continue
            </Button>
          </>
        }
      />
    </div>
  );
}

export function WizardSelectStep() {
  const { orgId, canManage } = useOrg();
  const navigate = useWizardNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const listId = useId();
  const [wizardState, setWizard] = useState(() => loadWizard(orgId));
  const [pickedSource, setPickedSource] = useState<WizardSourceId | null>(
    () => loadWizard(orgId).source ?? null,
  );
  const [connectionChecked, setConnectionChecked] = useState(false);
  const [events, setEvents] = useState<SourceEventItem[]>([]);
  const [connectionId, setConnectionId] = useState<string | undefined>();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(() =>
    wizardState.event ? eventKey(wizardState.event) : null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [continueBusy, setContinueBusy] = useState(false);
  const [registrationPromptOpen, setRegistrationPromptOpen] = useState(false);
  const [enableRegistrationBusy, setEnableRegistrationBusy] = useState(false);
  const [connectionRefresh, setConnectionRefresh] = useState(0);
  const [pasteFormUrl, setPasteFormUrl] = useState("");
  const [pasteBusy, setPasteBusy] = useState(false);

  const activeSource = wizardState.source;
  const sourceConfirmed =
    activeSource === "zoom" ||
    activeSource === "google_meet" ||
    activeSource === "google_forms" ||
    activeSource === "linkedin_events" ||
    activeSource === "heysummit" ||
    activeSource === "fluent_forms" ||
    activeSource === "constant_contact" ||
    activeSource === "mailchimp" ||
    activeSource === "eventbrite" ||
    activeSource === "microsoft_teams";
  const showEvents = sourceConfirmed && wizardState.sourceConnected;
  const showConnect = sourceConfirmed && !wizardState.sourceConnected;

  const zoomReturn = searchParams.get("zoom");
  const zoomReturnReason = searchParams.get("reason");
  const linkedinReturn = searchParams.get("linkedin");
  const constantContactReturn = searchParams.get("constantcontact");
  const mailchimpReturn = searchParams.get("mailchimp");
  const eventbriteReturn = searchParams.get("eventbrite");
  const microsoftTeamsReturn = searchParams.get("microsoft_teams");
  const googleFormsReturn = searchParams.get("google_forms");

  useEffect(() => {
    if (!zoomReturn) return;
    if (zoomReturn === "error") {
      const reason = zoomReturnReason ?? "failed";
      const messages: Record<string, string> = {
        missing_code:
          "Zoom did not return authorization details. Please try again.",
        expired: "The Zoom authorization session expired. Please try again.",
        organization_required:
          "This connection link is not tied to an organization.",
        zoom_not_configured: "Zoom is not configured on this server.",
        wrong_account:
          "Please connect the Zoom account that matches the invite.",
        already_connected:
          "That Zoom account is already connected to another organization.",
        failed: "Zoom connection failed. Please try again.",
      };
      setError(messages[reason] ?? messages.failed!);
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("zoom") && !prev.has("reason")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("zoom");
        next.delete("reason");
        return next;
      },
      { replace: true },
    );
  }, [zoomReturn, zoomReturnReason, setSearchParams]);

  useEffect(() => {
    if (!linkedinReturn) return;
    if (linkedinReturn === "error") {
      setError("LinkedIn connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("linkedin")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("linkedin");
        return next;
      },
      { replace: true },
    );
  }, [linkedinReturn, setSearchParams]);

  useEffect(() => {
    if (!constantContactReturn) return;
    if (constantContactReturn === "error") {
      setError("Constant Contact connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("constantcontact")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("constantcontact");
        return next;
      },
      { replace: true },
    );
  }, [constantContactReturn, setSearchParams]);

  useEffect(() => {
    if (!mailchimpReturn) return;
    if (mailchimpReturn === "error") {
      setError("Mailchimp connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("mailchimp") && !prev.has("reason")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("mailchimp");
        next.delete("reason");
        return next;
      },
      { replace: true },
    );
  }, [mailchimpReturn, setSearchParams]);

  useEffect(() => {
    if (!eventbriteReturn) return;
    if (eventbriteReturn === "error") {
      setError("Eventbrite connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("eventbrite") && !prev.has("reason")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("eventbrite");
        next.delete("reason");
        return next;
      },
      { replace: true },
    );
  }, [eventbriteReturn, setSearchParams]);

  useEffect(() => {
    if (!microsoftTeamsReturn) return;
    if (microsoftTeamsReturn === "error") {
      setError("Microsoft Teams connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("microsoft_teams") && !prev.has("reason")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("microsoft_teams");
        next.delete("reason");
        return next;
      },
      { replace: true },
    );
  }, [microsoftTeamsReturn, setSearchParams]);

  useEffect(() => {
    if (!googleFormsReturn) return;
    if (googleFormsReturn === "error") {
      setError("Google Forms connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (!prev.has("google_forms")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("google_forms");
        return next;
      },
      { replace: true },
    );
  }, [googleFormsReturn, setSearchParams]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") {
        setConnectionRefresh((n) => n + 1);
      }
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const provider = loadWizard(orgId).source;
    if (!provider) {
      setConnectionChecked(true);
      return;
    }
    void api<{ connections: Array<{ id: string; status: string }> }>(
      `/api/accounts/${orgId}/sources/${provider}`,
    )
      .then((r) => {
        if (cancelled) return;
        const connected = (r.connections ?? []).some(
          (c) => c.status === "active",
        );
        setWizard((prev) => {
          const next = { ...prev, sourceConnected: connected };
          saveWizard(orgId, next);
          return next;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setWizard((prev) => {
          const next = { ...prev, sourceConnected: false };
          saveWizard(orgId, next);
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setConnectionChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, connectionRefresh, wizardState.source]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!connectionChecked || !showEvents) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ type: "all" });
    if (debouncedQuery) params.set("query", debouncedQuery);

    void api<{ connectionId: string; items: SourceEventItem[] }>(
      `/api/accounts/${orgId}/sources/${activeSource}/events?${params.toString()}`,
    )
      .then((r) => {
        if (cancelled) return;
        setConnectionId(r.connectionId);
        setEvents(r.items ?? []);
        setWizard((prev) => {
          const next = { ...prev, sourceConnected: true };
          saveWizard(orgId, next);
          return next;
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setEvents([]);
        setError(e instanceof Error ? e.message : "Could not load events");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, debouncedQuery, connectionChecked, showEvents, activeSource]);

  function clearSourceChoice() {
    const next: WizardState = { ...loadWizard(orgId), source: undefined };
    saveWizard(orgId, next);
    setWizard(next);
    setPickedSource(null);
  }

  function confirmSource() {
    if (!pickedSource) return;
    const next: WizardState = {
      ...loadWizard(orgId),
      source: pickedSource,
      sourceConnected: false,
    };
    saveWizard(orgId, next);
    setWizard(next);
    setConnectionChecked(false);
    setConnectionRefresh((n) => n + 1);
  }

  function skipSourceForLocalDev() {
    if (!isLocalDevWizard()) return;
    const next: WizardState = {
      ...loadWizard(orgId),
      source: undefined,
      sourceConnected: false,
      event: undefined,
    };
    saveWizard(orgId, next);
    setWizard(next);
    navigate(`/conferences/new/destinations`);
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  if (!connectionChecked) {
    return (
      <div className="rs-connect-step">
        <div className="rs-connect-main">
          <Skeleton className="h-40 w-full max-w-md" />
        </div>
        <WizardStepFooter
          onBack={() => navigate(`/conferences/new/introduction`)}
        />
      </div>
    );
  }

  if (!sourceConfirmed) {
    return (
      <ChooseSourcePanel
        selectedSource={pickedSource}
        onSelectSource={setPickedSource}
        onBack={() => navigate(`/conferences/new/introduction`)}
        onContinue={() => confirmSource()}
        onSkipSource={skipSourceForLocalDev}
      />
    );
  }

  if (showConnect) {
    if (activeSource === "google_meet") {
      return (
        <ConnectGoogleMeetPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
        />
      );
    }
    if (activeSource === "google_forms") {
      return (
        <ConnectGoogleFormsPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
        />
      );
    }
    if (activeSource === "linkedin_events") {
      return (
        <ConnectLinkedInEventsPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
        />
      );
    }
    if (activeSource === "constant_contact") {
      return (
        <ConnectConstantContactPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
        />
      );
    }
    if (activeSource === "heysummit") {
      return (
        <ConnectHeySummitPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
          onConnected={() => {
            const next: WizardState = {
              ...loadWizard(orgId),
              source: "heysummit",
              sourceConnected: true,
            };
            saveWizard(orgId, next);
            setWizard(next);
            setConnectionRefresh((n) => n + 1);
          }}
        />
      );
    }
    if (activeSource === "fluent_forms") {
      return (
        <ConnectFluentFormsPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
          onConnected={() => {
            const next: WizardState = {
              ...loadWizard(orgId),
              source: "fluent_forms",
              sourceConnected: true,
            };
            saveWizard(orgId, next);
            setWizard(next);
            setConnectionRefresh((n) => n + 1);
          }}
        />
      );
    }
    if (activeSource === "mailchimp") {
      return (
        <ConnectMailchimpPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
          onConnected={() => {
            const next: WizardState = {
              ...loadWizard(orgId),
              source: "mailchimp",
              sourceConnected: true,
            };
            saveWizard(orgId, next);
            setWizard(next);
            setConnectionRefresh((n) => n + 1);
          }}
        />
      );
    }
    if (activeSource === "eventbrite") {
      return (
        <ConnectEventbritePanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
          onConnected={() => {
            const next: WizardState = {
              ...loadWizard(orgId),
              source: "eventbrite",
              sourceConnected: true,
            };
            saveWizard(orgId, next);
            setWizard(next);
            setConnectionRefresh((n) => n + 1);
          }}
        />
      );
    }
    if (activeSource === "microsoft_teams") {
      return (
        <ConnectMicrosoftTeamsPanel
          onBack={() => clearSourceChoice()}
          onSkipSource={skipSourceForLocalDev}
          error={error}
        />
      );
    }
    return (
      <ConnectZoomPanel
        onBack={() => clearSourceChoice()}
        onSkipSource={skipSourceForLocalDev}
        error={error}
      />
    );
  }

  function selectEvent(ev: SourceEventItem) {
    if (ev.alreadyConfigured) return;
    setSelectedKey(eventKey(ev));
    setError(null);
    setRegistrationPromptOpen(false);
  }

  const selectedEvent = events.find(
    (ev) => eventKey(ev) === selectedKey && !ev.alreadyConfigured,
  );

  async function persistSelectionAndContinue(
    ev: SourceEventItem,
    sourceConnectionId: string,
  ) {
    const prev = loadWizard(orgId);
    const next: WizardState = {
      ...prev,
      streamName: prev.streamName?.trim() || (ev.name?.trim() ?? ""),
      source: activeSource ?? "zoom",
      sourceConnected: true,
      event: {
        id: ev.id,
        resourceType: ev.resourceType,
        name: ev.name,
        startTime: ev.startTime,
        connectionId: sourceConnectionId,
      },
    };
    saveWizard(orgId, next);
    setWizard(next);

    // Skip destinations when exactly one CRM credential is already configured.
    try {
      const schemas = await api<{ providers: Array<{ provider: string }> }>(
        `/api/providers/setup-schemas`,
      );
      const setups = await Promise.all(
        (schemas.providers ?? []).map((p) =>
          api<{
            provider: string;
            configured: boolean;
            credentialId: string | null;
          }>(
            `/api/accounts/${orgId}/providers/${encodeURIComponent(p.provider)}/setup`,
          ),
        ),
      );
      const ready = setups.filter((p) => p.configured && p.credentialId);
      if (ready.length === 1) {
        const only = ready[0]!;
        const withDest: WizardState = {
          ...next,
          destinationProvider: only.provider,
          destinationConfigured: true,
          destinationCredentialId: only.credentialId,
        };
        saveWizard(orgId, withDest);
        navigate(`/conferences/new/configure`);
        return;
      }
    } catch {
      /* fall through to destinations */
    }
    navigate(`/conferences/new/destinations`);
  }

  function continueWithSelection() {
    if (!selectedEvent || !connectionId || continueBusy) return;
    setContinueBusy(true);
    setError(null);
    void (async () => {
      try {
        await api(
          `/api/accounts/${orgId}/sources/${activeSource}/events/verify`,
          {
            method: "POST",
            body: JSON.stringify({
              connectionId,
              resourceType: selectedEvent.resourceType,
              resourceId: selectedEvent.id,
            }),
          },
        );
        await persistSelectionAndContinue(selectedEvent, connectionId);
      } catch (e) {
        if (
          activeSource === "zoom" &&
          e instanceof ApiError &&
          e.code === "ZOOM_REGISTRATION_DISABLED"
        ) {
          // Primary path: enable registration automatically, then continue.
          try {
            await api(
              `/api/accounts/${orgId}/sources/zoom/events/enable-registration`,
              {
                method: "POST",
                body: JSON.stringify({
                  connectionId,
                  events: [
                    {
                      resourceType: selectedEvent.resourceType,
                      resourceId: selectedEvent.id,
                    },
                  ],
                }),
              },
            );
            await api(`/api/accounts/${orgId}/sources/zoom/events/verify`, {
              method: "POST",
              body: JSON.stringify({
                connectionId,
                resourceType: selectedEvent.resourceType,
                resourceId: selectedEvent.id,
              }),
            });
            await persistSelectionAndContinue(selectedEvent, connectionId);
            return;
          } catch {
            setRegistrationPromptOpen(true);
            return;
          }
        }
        setError(
          e instanceof Error
            ? e.message
            : "Could not verify this event. Fix the connection and try again.",
        );
      } finally {
        setContinueBusy(false);
      }
    })();
  }

  function continueWithPastedForm() {
    if (
      activeSource !== "google_forms" ||
      !connectionId ||
      pasteBusy ||
      continueBusy
    )
      return;
    const raw = pasteFormUrl.trim();
    if (!raw) {
      setError("Paste a Google Form edit URL or file ID.");
      return;
    }
    setPasteBusy(true);
    setError(null);
    void (async () => {
      try {
        const verified = await api<{
          resourceId: string;
          formTitle?: string | null;
        }>(`/api/accounts/${orgId}/sources/google_forms/events/verify`, {
          method: "POST",
          body: JSON.stringify({
            connectionId,
            formUrl: raw,
            resourceId: raw,
          }),
        });
        const ev: SourceEventItem = {
          id: verified.resourceId,
          resourceType: "form",
          name: verified.formTitle?.trim() || `Form ${verified.resourceId}`,
          startTime: null,
          alreadyConfigured: false,
        };
        await persistSelectionAndContinue(ev, connectionId);
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : "Could not verify this form. Use an edit URL and ensure Collect email addresses is on.",
        );
      } finally {
        setPasteBusy(false);
      }
    })();
  }

  function enableRegistrationOnSelected() {
    if (!selectedEvent || !connectionId || enableRegistrationBusy) return;
    setEnableRegistrationBusy(true);
    setError(null);
    void (async () => {
      try {
        await api(
          `/api/accounts/${orgId}/sources/zoom/events/enable-registration`,
          {
            method: "POST",
            body: JSON.stringify({
              connectionId,
              events: [
                {
                  resourceType: selectedEvent.resourceType,
                  resourceId: selectedEvent.id,
                },
              ],
            }),
          },
        );
        await api(`/api/accounts/${orgId}/sources/zoom/events/verify`, {
          method: "POST",
          body: JSON.stringify({
            connectionId,
            resourceType: selectedEvent.resourceType,
            resourceId: selectedEvent.id,
          }),
        });
        setRegistrationPromptOpen(false);
        await persistSelectionAndContinue(selectedEvent, connectionId);
      } catch (e) {
        setRegistrationPromptOpen(false);
        setError(
          e instanceof Error
            ? e.message
            : "Could not enable registration on this Zoom event. Try again in Zoom or pick another meeting.",
        );
      } finally {
        setEnableRegistrationBusy(false);
      }
    })();
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-wizard-select-main">
        <div className="rs-wizard-select-header">
          <h2 className="text-rs-ink">Select a Conference</h2>
          <p className="text-sm text-rs-ink-muted">
            {activeSource === "google_meet"
              ? "Choose a Google Calendar event or appointment with attendees."
              : activeSource === "google_forms"
                ? "Paste a Google Form edit URL or file ID. Forms must collect email addresses."
                : activeSource === "linkedin_events"
                  ? "Choose a LinkedIn Company Page event with Lead Gen registration."
                  : activeSource === "heysummit"
                    ? "Choose a HeySummit event to sync attendees."
                    : activeSource === "fluent_forms"
                      ? "Choose a Fluent Forms form to sync submissions."
                      : activeSource === "constant_contact"
                        ? "Choose a Constant Contact event to sync registrants."
                        : activeSource === "mailchimp"
                          ? "Choose a Mailchimp audience to sync subscribed members."
                          : activeSource === "eventbrite"
                            ? "Choose an Eventbrite event to sync attendees."
                            : activeSource === "microsoft_teams"
                              ? "Choose a Teams webinar or Outlook calendar appointment to sync."
                              : "Choose one upcoming Zoom meeting or webinar to sync."}
          </p>
          {activeSource === "google_forms" ? (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-rs-ink">
                Paste Form URL / ID
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  className="flex-1"
                  placeholder="https://docs.google.com/forms/d/…/edit"
                  value={pasteFormUrl}
                  onChange={(e) => setPasteFormUrl(e.target.value)}
                  aria-label="Paste Form URL or ID"
                />
                <Button
                  variant="secondary"
                  disabled={
                    !pasteFormUrl.trim() ||
                    pasteBusy ||
                    continueBusy ||
                    !connectionId
                  }
                  onClick={() => void continueWithPastedForm()}
                >
                  {pasteBusy ? "Verifying…" : "Use Pasted Form"}
                </Button>
              </div>
              <p className="text-xs text-rs-ink-muted">
                Use the form edit link (not the published /d/e/ responder link).
              </p>
            </div>
          ) : (
            <>
              <Input
                className="mt-4"
                placeholder={
                  activeSource === "google_meet"
                    ? "Search calendar events…"
                    : activeSource === "linkedin_events"
                      ? "Search LinkedIn events…"
                      : activeSource === "heysummit"
                        ? "Search HeySummit events…"
                        : activeSource === "fluent_forms"
                          ? "Search Fluent Forms…"
                          : activeSource === "constant_contact"
                            ? "Search Constant Contact events…"
                            : activeSource === "mailchimp"
                              ? "Search Mailchimp audiences…"
                              : activeSource === "eventbrite"
                                ? "Search Eventbrite events…"
                                : activeSource === "microsoft_teams"
                                  ? "Search Teams webinars and appointments…"
                                  : "Search meetings and webinars…"
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={
                  activeSource === "fluent_forms"
                    ? "Search Forms"
                    : "Search Events"
                }
              />
            </>
          )}
        </div>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-rs-danger">
            {error}
          </p>
        ) : null}

        {activeSource !== "google_forms" ? (
          <div
            className="rs-wizard-event-list"
            role="radiogroup"
            aria-labelledby={listId}
          >
            <span id={listId} className="sr-only">
              Upcoming Zoom Events
            </span>
            {loading ? (
              <div className="space-y-2 p-1">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : events.length === 0 ? (
              <div className="rs-wizard-event-empty">
                <Calendar
                  aria-hidden="true"
                  className="mx-auto mb-3 h-8 w-8 text-rs-ink-subtle"
                />
                <p className="text-sm font-medium text-rs-ink">
                  {activeSource === "google_meet"
                    ? "No Calendar Events Found"
                    : "No Upcoming Events"}
                </p>
                <p className="mt-1 text-sm text-rs-ink-muted">
                  {activeSource === "google_meet"
                    ? "Create a Google Calendar event with attendees, then try again."
                    : "Schedule a meeting or webinar in Zoom, then try again."}
                </p>
              </div>
            ) : (
              events.map((ev) => {
                const key = eventKey(ev);
                const selected = selectedKey === key;
                const disabled = Boolean(ev.alreadyConfigured);
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    className={cn(
                      "rs-wizard-event-option",
                      selected && "rs-wizard-event-option-selected",
                      disabled && "rs-wizard-event-option-disabled",
                    )}
                    onClick={() => selectEvent(ev)}
                  >
                    <span
                      className="rs-wizard-event-radio"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block truncate font-medium text-rs-ink">
                        {ev.name}
                      </span>
                      <span className="mt-0.5 block truncate text-sm capitalize text-rs-ink-muted">
                        {formatEventMeta(ev)}
                      </span>
                    </span>
                    {disabled ? (
                      <StatusBadge tone="completed">
                        Already Configured
                      </StatusBadge>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        ) : null}
      </div>

      <WizardStepFooter
        onBack={() => clearSourceChoice()}
        primary={
          <Button
            disabled={
              !selectedEvent || loading || continueBusy || !connectionId
            }
            onClick={() => continueWithSelection()}
          >
            {continueBusy ? "Verifying…" : "Continue"}
          </Button>
        }
      />

      <ConfirmDialog
        open={registrationPromptOpen}
        onOpenChange={(open) => {
          if (!enableRegistrationBusy) setRegistrationPromptOpen(open);
        }}
        title="Enable Registration"
        description={
          selectedEvent
            ? `AttendeeSync could not turn on registration automatically for "${selectedEvent.name}". Enable it now so attendees can sync?`
            : "AttendeeSync could not turn on registration automatically. Enable it now so attendees can sync?"
        }
        impact="Zoom will require attendees to register before joining. You can change this later in Zoom."
        confirmLabel={
          enableRegistrationBusy ? "Enabling…" : "Enable Registration"
        }
        busy={enableRegistrationBusy}
        onConfirm={() => enableRegistrationOnSelected()}
      />
    </div>
  );
}

export function WizardDestinationsStep() {
  const { orgId, canManage } = useOrg();
  const navigate = useWizardNavigate();

  const initialWizard = loadWizard(orgId);
  const [wizardState, setWizard] = useState<WizardState>(() => initialWizard);
  const [pickedDestination, setPickedDestination] = useState<string | null>(
    () => initialWizard.destinationProvider ?? null,
  );

  const listId = useId();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<DestinationProviderSetup[]>([]);

  const confirmedDestination = wizardState.destinationProvider;
  const confirmedSetup =
    confirmedDestination != null
      ? (providers.find((p) => p.provider === confirmedDestination) ?? null)
      : null;

  // Credentials form (for unconfigured destination providers)
  const [secretsDraft, setSecretsDraft] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [revealedSecrets, setRevealedSecrets] = useState<
    Record<string, boolean>
  >({});
  const [forceCredentials, setForceCredentials] = useState(false);
  const [pendingCredentialError, setPendingCredentialError] = useState<
    string | null
  >(null);
  const [continueBusy, setContinueBusy] = useState(false);

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    void api<{
      providers: Array<{
        provider: string;
        status?: "ga" | "beta" | "disabled";
      }>;
    }>(`/api/providers/setup-schemas`)
      .then(async (r) => {
        const list = r.providers ?? [];
        const statusByProvider = new Map(
          list.map((p) => [p.provider, p.status ?? "ga"] as const),
        );
        const setups = await Promise.all(
          list.map((p) =>
            api<DestinationProviderSetup>(
              `/api/accounts/${orgId}/providers/${encodeURIComponent(p.provider)}/setup`,
            ),
          ),
        );
        if (cancelled) return;
        setProviders(
          setups.map((s) => ({
            ...s,
            status: statusByProvider.get(s.provider) ?? "ga",
          })),
        );

        // Auto-skip when exactly one destination is already configured.
        const ready = setups.filter((p) => p.configured && p.credentialId);
        const current = loadWizard(orgId);
        if (ready.length === 1 && !current.destinationProvider) {
          const only = ready[0]!;
          const next: WizardState = {
            ...current,
            destinationProvider: only.provider,
            destinationConfigured: true,
            destinationCredentialId: only.credentialId,
          };
          saveWizard(orgId, next);
          navigate(`/conferences/new/configure`);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : "Could not load destinations",
        );
        setProviders([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, canManage]);

  useEffect(() => {
    // Keep the radio selection aligned with the confirmed wizard state.
    setPickedDestination(wizardState.destinationProvider ?? null);
  }, [wizardState.destinationProvider]);

  const showCredentials =
    confirmedDestination != null &&
    confirmedSetup != null &&
    (!confirmedSetup.configured || forceCredentials);

  useEffect(() => {
    if (!showCredentials || !confirmedSetup) return;
    setSecretsDraft({});
    setFieldErrors({});
    setSaveError(pendingCredentialError);
    setPendingCredentialError(null);
    setSaveBusy(false);
    setRevealedSecrets({});
    // pendingCredentialError is intentionally applied once when the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedDestination, confirmedSetup?.provider, showCredentials]);

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can add conferences.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="rs-connect-step">
        <div className="rs-connect-main">
          <div className="w-full px-6">
            <Skeleton className="h-40 w-full max-w-md" />
          </div>
        </div>
        <WizardStepFooter onBack={() => navigate(`/conferences/new/select`)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rs-connect-step">
        <div className="rs-connect-main">
          <Panel className="rs-connect-copy">
            <h2 className="text-rs-ink">Choose Your Destination</h2>
            <p className="text-sm text-rs-ink-muted">
              Could not load destination options.
            </p>
            <p role="alert" className="mt-3 text-sm text-rs-danger">
              {error}
            </p>
          </Panel>
        </div>
        <WizardStepFooter onBack={() => navigate(`/conferences/new/select`)} />
      </div>
    );
  }

  if (showCredentials && confirmedSetup) {
    const credentialFields = confirmedSetup.fields;
    const requiredFields = credentialFields.filter((f) => f.required);
    const isFormValid = requiredFields.every((f) =>
      Boolean(secretsDraft[f.key]?.trim()),
    );
    const findHints = credentialFields.filter((f) => Boolean(f.help?.trim()));
    const intro =
      confirmedSetup.description?.trim() ||
      `Add the required account details for ${confirmedSetup.label}. Values are encrypted at rest and never shown in plaintext again.`;

    async function saveCredentials() {
      if (!confirmedSetup || !confirmedDestination) return;
      setFieldErrors({});
      setSaveError(null);

      const missing = requiredFields.filter(
        (f) => !secretsDraft[f.key]?.trim(),
      );
      if (missing.length) {
        setFieldErrors(
          missing.reduce<Record<string, string>>((acc, f) => {
            acc[f.key] = `${f.label} is required.`;
            return acc;
          }, {}),
        );
        return;
      }

      setSaveBusy(true);
      try {
        const secrets: Record<string, string> = {};
        for (const f of credentialFields) {
          const v = secretsDraft[f.key]?.trim();
          if (v) secrets[f.key] = v;
        }

        const result = await api<DestinationCredentialUpsertResult>(
          `/api/accounts/${orgId}/providers/${encodeURIComponent(confirmedDestination)}/setup`,
          {
            method: "PUT",
            body: JSON.stringify({ secrets }),
          },
        );

        const next: WizardState = {
          ...loadWizard(orgId),
          destinationProvider: confirmedDestination,
          destinationConfigured: true,
          destinationCredentialId: result.id,
        };
        saveWizard(orgId, next);
        setWizard(next);

        setProviders((prev) =>
          prev.map((p) =>
            p.provider === confirmedDestination
              ? { ...p, configured: true, hintMask: result.hintMask }
              : p,
          ),
        );

        setSecretsDraft({});
        setFieldErrors({});
        setRevealedSecrets({});
        setForceCredentials(false);
        setPendingCredentialError(null);
        navigate(`/conferences/new/configure`);
      } catch (e) {
        setSaveError(
          e instanceof Error ? e.message : "Could not save credentials",
        );
      } finally {
        setSaveBusy(false);
      }
    }

    return (
      <div className="rs-connect-step">
        <div className="rs-wizard-select-main">
          <div className="rs-wizard-select-header">
            <h2 className="text-rs-ink">
              Enter {confirmedSetup.label} Credentials
            </h2>
            <p className="text-sm text-rs-ink-muted">{intro}</p>
          </div>

          <div className="rs-wizard-event-list">
            <div className="w-full max-w-xl space-y-4">
              <Panel className="p-5">
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveCredentials();
                  }}
                >
                  {credentialFields.map((f) => {
                    const errorForField = fieldErrors[f.key];
                    const revealed = Boolean(revealedSecrets[f.key]);
                    return (
                      <div key={f.key} className="space-y-1.5">
                        {f.secret ? (
                          <div className="relative">
                            <Input
                              name={f.key}
                              placeholder={f.label}
                              aria-label={f.label}
                              autoComplete="off"
                              type={revealed ? "text" : "password"}
                              value={secretsDraft[f.key] ?? ""}
                              disabled={saveBusy}
                              aria-invalid={Boolean(errorForField)}
                              className="pr-10"
                              onChange={(e) => {
                                const next = e.target.value;
                                setSecretsDraft((prev) => ({
                                  ...prev,
                                  [f.key]: next,
                                }));
                                setFieldErrors((prev) => {
                                  if (!prev[f.key]) return prev;
                                  const updated = { ...prev };
                                  delete updated[f.key];
                                  return updated;
                                });
                              }}
                            />
                            <button
                              type="button"
                              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-rs-ink-subtle hover:text-rs-ink-muted disabled:opacity-50"
                              disabled={saveBusy}
                              onClick={() =>
                                setRevealedSecrets((prev) => ({
                                  ...prev,
                                  [f.key]: !prev[f.key],
                                }))
                              }
                              aria-label={
                                revealed ? "Hide Password" : "Show Password"
                              }
                              aria-pressed={revealed}
                            >
                              {revealed ? (
                                <EyeOff className="size-4" aria-hidden />
                              ) : (
                                <Eye className="size-4" aria-hidden />
                              )}
                            </button>
                          </div>
                        ) : (
                          <Input
                            name={f.key}
                            placeholder={f.label}
                            aria-label={f.label}
                            autoComplete="off"
                            type="text"
                            value={secretsDraft[f.key] ?? ""}
                            disabled={saveBusy}
                            aria-invalid={Boolean(errorForField)}
                            onChange={(e) => {
                              const next = e.target.value;
                              setSecretsDraft((prev) => ({
                                ...prev,
                                [f.key]: next,
                              }));
                              setFieldErrors((prev) => {
                                if (!prev[f.key]) return prev;
                                const updated = { ...prev };
                                delete updated[f.key];
                                return updated;
                              });
                            }}
                          />
                        )}
                        {errorForField ? (
                          <p role="alert" className="text-sm text-rs-danger">
                            {errorForField}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </form>

                {saveError ? (
                  <p role="alert" className="mt-3 text-sm text-rs-danger">
                    {saveError}
                  </p>
                ) : null}
              </Panel>

              {findHints.length > 0 ||
              confirmedSetup.recommendation ||
              confirmedSetup.docsUrl ? (
                <div className="space-y-3 text-sm text-rs-ink-muted">
                  {findHints.length > 0 ? (
                    <div>
                      <p>Find these values in {confirmedSetup.label}:</p>
                      <ul className="mt-1.5 list-disc space-y-1 pl-5">
                        {findHints.map((f) => (
                          <li key={f.key}>
                            {f.label}: {f.help}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {confirmedSetup.recommendation ? (
                    <p>{confirmedSetup.recommendation}</p>
                  ) : null}
                  {confirmedSetup.docsUrl ? (
                    <p>
                      <a
                        href={confirmedSetup.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-rs-accent hover:text-rs-accent-hover"
                      >
                        {confirmedSetup.docsLabel?.trim() ||
                          `View ${confirmedSetup.label} setup instructions`}{" "}
                        ↗
                      </a>
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <WizardStepFooter
          onBack={() => {
            const next: WizardState = {
              ...loadWizard(orgId),
              destinationProvider: undefined,
              destinationConfigured: false,
              destinationCredentialId: null,
            };
            saveWizard(orgId, next);
            setWizard(next);
            setPickedDestination(null);
            setForceCredentials(false);
            setPendingCredentialError(null);
            setSaveError(null);
          }}
          primary={
            <Button
              disabled={!isFormValid || saveBusy}
              onClick={() => void saveCredentials()}
            >
              {saveBusy ? "Verifying Credentials…" : "Save Credentials"}
            </Button>
          }
        />
      </div>
    );
  }

  const selectedSetup =
    pickedDestination != null
      ? providers.find((p) => p.provider === pickedDestination)
      : null;

  return (
    <div className="rs-connect-step">
      <div className="rs-wizard-select-main">
        <div className="rs-wizard-select-header">
          <h2 className="text-rs-ink">Choose Your Destination</h2>
          <p className="text-sm text-rs-ink-muted">
            Select where AttendeeSync should send attendee and registration
            information for this sync stream.
          </p>
          <p
            id={`${listId}-heading`}
            className="mt-4 text-sm font-medium text-rs-ink"
          >
            Choose the CRM you want to connect:
          </p>
        </div>

        <div
          className="rs-wizard-event-list"
          role="radiogroup"
          aria-labelledby={`${listId}-heading`}
        >
          {providers.map((p) => {
            const selected = pickedDestination === p.provider;
            return (
              <button
                key={p.provider}
                type="button"
                role="radio"
                aria-checked={selected}
                className={cn(
                  "rs-wizard-source-option",
                  selected && "rs-wizard-source-option-selected",
                )}
                onClick={() => setPickedDestination(p.provider)}
              >
                <span className="rs-wizard-event-radio" aria-hidden="true" />
                <ProviderLogo
                  provider={p.provider}
                  label={p.label}
                  className="mt-px size-9"
                />
                <span className="min-w-0 flex-1 text-left">
                  <span className="flex items-center gap-2">
                    <span className="block font-medium text-rs-ink">
                      {p.label}
                    </span>
                    {p.status === "beta" ? (
                      <StatusBadge tone="needs_configuration">Beta</StatusBadge>
                    ) : null}
                    {p.configured ? (
                      <StatusBadge tone="ready">Connected</StatusBadge>
                    ) : (
                      <StatusBadge tone="needs_configuration">
                        Set Up
                      </StatusBadge>
                    )}
                  </span>
                  <span className="mt-0.5 block text-sm text-rs-ink-muted">
                    {p.configured
                      ? p.hintMask
                        ? `Credentials saved: ${p.hintMask}`
                        : "Credentials saved"
                      : "Not configured yet — you'll add credentials next."}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <WizardStepFooter
        onBack={() => navigate(`/conferences/new/select`)}
        primary={
          <Button
            disabled={
              !selectedSetup || pickedDestination == null || continueBusy
            }
            onClick={() => {
              if (!pickedDestination) return;
              const setup = providers.find(
                (p) => p.provider === pickedDestination,
              );
              void (async () => {
                const next: WizardState = {
                  ...loadWizard(orgId),
                  destinationProvider: pickedDestination,
                  destinationConfigured: Boolean(setup?.configured),
                  destinationCredentialId: setup?.credentialId ?? null,
                };
                saveWizard(orgId, next);
                setWizard(next);

                if (!setup?.configured) {
                  setForceCredentials(false);
                  return;
                }

                setContinueBusy(true);
                setPendingCredentialError(null);
                try {
                  await api(
                    `/api/accounts/${orgId}/providers/${encodeURIComponent(pickedDestination)}/setup/verify`,
                    { method: "POST", body: "{}" },
                  );
                  setForceCredentials(false);
                  navigate(`/conferences/new/configure`);
                } catch (e) {
                  setForceCredentials(true);
                  setPendingCredentialError(
                    e instanceof Error
                      ? e.message
                      : "Destination credentials could not be verified. Enter them again.",
                  );
                  // Mark unconfigured locally so the credentials form stays open until re-saved.
                  setProviders((prev) =>
                    prev.map((p) =>
                      p.provider === pickedDestination
                        ? { ...p, configured: false }
                        : p,
                    ),
                  );
                  setWizard({
                    ...next,
                    destinationConfigured: false,
                  });
                  saveWizard(orgId, {
                    ...next,
                    destinationConfigured: false,
                  });
                } finally {
                  setContinueBusy(false);
                }
              })();
            }}
          >
            {continueBusy ? "Verifying…" : "Continue"}
          </Button>
        }
      />
    </div>
  );
}

export { WizardConfigureStep } from "./configure-sync-step";

export function WizardReviewStep() {
  const { orgId, canManage } = useOrg();
  const navigate = useWizardNavigate();
  const appNavigate = useNavigate();
  const wizard = loadWizard(orgId);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summary = wizard.configure
    ? summarizeConfigureSync(wizard.configure)
    : null;

  const canActivate = Boolean(
    wizard.streamName?.trim() &&
    (wizard.source === "zoom" ||
      wizard.source === "google_meet" ||
      wizard.source === "google_forms" ||
      wizard.source === "linkedin_events" ||
      wizard.source === "heysummit" ||
      wizard.source === "fluent_forms" ||
      wizard.source === "constant_contact" ||
      wizard.source === "mailchimp" ||
      wizard.source === "eventbrite" ||
      wizard.source === "microsoft_teams") &&
    wizard.event?.id &&
    wizard.event.resourceType &&
    wizard.event.connectionId &&
    wizard.destinationProvider &&
    // The demo-only "Mock CRM" needs no credential to activate (it writes nothing).
    (wizard.destinationCredentialId ||
      wizard.destinationProvider === "mock_crm") &&
    wizard.configure,
  );

  async function activate() {
    if (!canActivate || activating) return;
    setActivating(true);
    setError(null);
    try {
      const confRes = await api<{ conference: { id: string } }>(
        `/api/accounts/${orgId}/conferences`,
        {
          method: "POST",
          body: JSON.stringify({ name: wizard.streamName.trim() }),
        },
      );
      const conferenceId = confRes.conference.id;

      const streamRes = await api<{ stream: { id: string } }>(
        `/api/accounts/${orgId}/conferences/${conferenceId}/streams`,
        {
          method: "POST",
          body: JSON.stringify({
            name: wizard.streamName.trim(),
            sourceProvider: wizard.source,
            destinationProvider: wizard.destinationProvider,
          }),
        },
      );
      const streamId = streamRes.stream.id;

      const intervalSeconds = scheduleToIntervalSeconds(
        wizard.configure!.schedule,
      );
      const mappingJson = JSON.stringify({
        resourceType: wizard.event!.resourceType,
        sourceResourceId: wizard.event!.id,
        zoomResourceId: wizard.event!.id,
        crmEventId: "",
        syncRegistration: true,
        syncAttendance: true,
        updateExisting:
          wizard.configure!.behavior.existingRecordAction !== "skip",
        createMissing:
          wizard.configure!.behavior.existingRecordAction !== "skip",
        fieldMappings: wizard.configure!.mappings,
        behavior: wizard.configure!.behavior,
        schedule: wizard.configure!.schedule,
      });

      await api(
        `/api/accounts/${orgId}/conferences/${conferenceId}/streams/${streamId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            destinationCredentialId: wizard.destinationCredentialId,
            sourceConnectionId: wizard.event!.connectionId,
            mappingJson,
            scheduleIntervalSeconds: intervalSeconds ?? 900,
            lifecycle: "active",
          }),
        },
      );

      clearWizard(orgId);
      appNavigate(`/conferences/${conferenceId}`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not activate conference";
      setError(message);
    } finally {
      setActivating(false);
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can review sync setup.
      </p>
    );
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-wizard-select-main">
        <div className="rs-wizard-select-header">
          <h2 className="text-rs-ink">Review &amp; Activate</h2>
          <p className="text-sm text-rs-ink-muted">
            Confirm your sync configuration, then create the conference and sync
            stream.
          </p>
        </div>

        <div className="rs-wizard-configure-sections">
          <section className="rs-wizard-configure-section">
            <h3>Stream</h3>
            <dl className="rs-wizard-review-dl">
              <div>
                <dt>Name</dt>
                <dd>{wizard.streamName || "—"}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>
                  {wizard.source === "zoom"
                    ? "Zoom"
                    : wizard.source === "google_meet"
                      ? "Google Meet"
                      : wizard.source === "google_forms"
                        ? "Google Forms"
                        : wizard.source === "linkedin_events"
                          ? "LinkedIn Events"
                          : wizard.source === "heysummit"
                            ? "HeySummit"
                            : wizard.source === "fluent_forms"
                              ? "Fluent Forms"
                              : wizard.source === "constant_contact"
                                ? "Constant Contact"
                                : wizard.source === "mailchimp"
                                  ? "Mailchimp"
                                  : wizard.source === "eventbrite"
                                    ? "Eventbrite"
                                    : wizard.source === "microsoft_teams"
                                      ? "Microsoft Teams"
                                      : "—"}
                </dd>
              </div>
              <div>
                <dt>Destination</dt>
                <dd>{wizard.destinationProvider || "—"}</dd>
              </div>
              {wizard.event ? (
                <div>
                  <dt>Event</dt>
                  <dd>{wizard.event.name}</dd>
                </div>
              ) : null}
            </dl>
          </section>

          {summary ? (
            <section className="rs-wizard-configure-section">
              <h3>Configure Sync</h3>
              <dl className="rs-wizard-review-dl">
                <div>
                  <dt>Schedule</dt>
                  <dd>{summary.schedule}</dd>
                </div>
                <div>
                  <dt>Data Mapping</dt>
                  <dd>{summary.dataMapping}</dd>
                </div>
                <div>
                  <dt>Existing Contacts</dt>
                  <dd>{summary.existingContacts}</dd>
                </div>
                <div>
                  <dt>Match By</dt>
                  <dd>{summary.matchBy}</dd>
                </div>
                <div>
                  <dt>Empty Values</dt>
                  <dd>{summary.emptyValues}</dd>
                </div>
              </dl>
            </section>
          ) : (
            <p className="text-sm text-rs-ink-muted">
              Sync preferences have not been configured yet.
            </p>
          )}

          {error ? <p className="text-sm text-rs-danger">{error}</p> : null}
          {!canActivate && !error ? (
            <p className="text-sm text-rs-ink-muted">
              Complete source, destination, and configure sync before
              activating.
            </p>
          ) : null}
        </div>
      </div>

      <WizardStepFooter
        onBack={() => navigate("/conferences/new/configure")}
        primary={
          <Button
            disabled={!canActivate || activating}
            onClick={() => void activate()}
          >
            {activating ? "Activating…" : "Activate"}
          </Button>
        }
      />
    </div>
  );
}
